import React, { Component } from "react";
import { Input } from "reactstrap";
import { getUserLoyalty, adjustLoyaltyPoints } from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import moment from "moment";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, MiniStat, StatRow, Table, Chip, Btn, Avatar, Field,
} from "../../components/Console/ConsoleUI";
import { canWrite } from "../../services/PermissionService";

/**
 * One loyalty member — balance, ledger and the manual adjustment, on the
 * console surface.
 *
 * The ledger is the point of the screen, so it takes the wide column; the
 * adjustment sits beside it because every adjustment is a judgement made while
 * reading that history.
 */

/** Ledger entry types carry a semantic tone rather than a bootstrap colour. */
const TYPE_TONE = {
  earn: "good",
  redeem: "bad",
  adjust: "warn",
  expire: "neutral",
};

const num = (n) => Number(n || 0).toLocaleString();
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/** Console-surface form fields. Inline styles cannot reach reactstrap's input. */
const CSS = `
.loy-form .form-control{height:46px;padding:0 15px;background:#fff;border:1px solid #e6d8c4;
  border-radius:12px;font-size:15px;color:${C.ink};box-shadow:none}
.loy-form .form-control:focus{border-color:${C.sand};box-shadow:none}
.loy-form textarea.form-control{height:auto;min-height:84px;padding:12px 15px;line-height:1.5;font-size:14px}
.loy-form .form-control::placeholder{color:${C.taupeLight}}
`;

class LoyaltyAccountDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoaded: false,
      isSaving: false,
      user: null,
      balance: null,
      history: [],
      adjustPoints: "",
      adjustNote: "",
    };
  }

  componentDidMount() {
    this.loadUser();
  }

  get userId() {
    return this.props.match.params.userId;
  }

  loadUser = () => {
    getUserLoyalty(this.userId)
      .then((result) => {
        if (result && result.status === 200 && result.data) {
          const d = result.data;
          this.setState({
            isLoaded: true,
            user: d.user || null,
            balance: {
              pointsBalance: d.pointsBalance ?? 0,
              totalEarned: d.totalEarned ?? 0,
              totalRedeemed: d.totalRedeemed ?? 0,
              lifetimeSpend: d.lifetimeSpend ?? 0,
              tier: d.tier || "standard",
            },
            history: d.history || [],
          });
        } else {
          this.setState({ isLoaded: true });
          toast.error((result && result.message) || "Failed to load member");
        }
      })
      .catch(() => {
        this.setState({ isLoaded: true });
        toast.error("Error loading member");
      });
  };

  handleAdjust = () => {
    const points = parseInt(this.state.adjustPoints, 10);
    if (!points || isNaN(points)) {
      toast.error("Enter a non-zero whole number of points (use a minus sign to deduct)");
      return;
    }

    this.setState({ isSaving: true });
    adjustLoyaltyPoints(this.userId, { points, note: this.state.adjustNote })
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Points adjusted");
          this.setState({ adjustPoints: "", adjustNote: "", isSaving: false });
          this.loadUser();
        } else {
          toast.error(response?.message || "Failed to adjust points");
          this.setState({ isSaving: false });
        }
      })
      .catch(() => {
        toast.error("Error adjusting points");
        this.setState({ isSaving: false });
      });
  };

  memberName() {
    const { user } = this.state;
    if (!user) return "";
    return `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unknown";
  }

  historyColumns() {
    return [
      {
        key: "createdAt",
        label: "When",
        width: 170,
        render: (t) => (
          <span style={{ color: C.taupe }}>
            {t.createdAt ? moment(t.createdAt).format("DD MMM YYYY, HH:mm") : "—"}
          </span>
        ),
      },
      {
        key: "type",
        label: "Type",
        width: 110,
        render: (t) => (
          <Chip tone={TYPE_TONE[t.type] || "neutral"}>
            <span style={{ textTransform: "capitalize" }}>{t.type || "—"}</span>
          </Chip>
        ),
      },
      {
        key: "points",
        label: "Points",
        align: "right",
        width: 100,
        render: (t) => (
          <span
            style={{
              fontSize: 15,
              fontWeight: 500,
              color: Number(t.points) < 0 ? C.red : "#2f7d33",
            }}
          >
            {Number(t.points) > 0 ? `+${t.points}` : t.points}
          </span>
        ),
      },
      {
        key: "balanceAfter",
        label: "Balance",
        align: "right",
        width: 100,
        render: (t) => <span style={{ color: C.ink }}>{t.balanceAfter ?? "—"}</span>,
      },
      {
        key: "source",
        label: "Source",
        width: 120,
        render: (t) => (
          <span style={{ color: C.taupe, textTransform: "capitalize" }}>{t.source || "—"}</span>
        ),
      },
      {
        key: "note",
        label: "Note",
        render: (t) => <span style={{ color: C.slate }}>{t.note || "—"}</span>,
      },
    ];
  }

  renderAdjustPanel() {
    const { isSaving, adjustPoints, adjustNote } = this.state;
    const write = canWrite();
    return (
      <Panel
        title="Manual adjustment"
        subtitle="Credits or deducts points immediately and writes a ledger entry"
      >
        <div className="loy-form" style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 16 }}>
          <Field label="Points" hint="Use a minus sign to deduct, e.g. −50">
            <Input
              type="number"
              step="1"
              placeholder="e.g. 100 or -50"
              value={adjustPoints}
              disabled={!write}
              onChange={(e) => this.setState({ adjustPoints: e.target.value })}
            />
          </Field>

          <Field label="Note" hint="Shown in the member's history">
            <Input
              type="textarea"
              rows={3}
              placeholder="Reason for this adjustment"
              value={adjustNote}
              disabled={!write}
              onChange={(e) => this.setState({ adjustNote: e.target.value })}
            />
          </Field>

          {write ? (
            <Btn
              icon="check"
              onClick={this.handleAdjust}
              disabled={isSaving}
              style={{ width: "100%" }}
            >
              {isSaving ? "Applying…" : "Apply adjustment"}
            </Btn>
          ) : (
            <div style={{ fontSize: 13, color: C.taupeLight }}>
              Your account can read this member's balance but not change it.
            </div>
          )}
        </div>
      </Panel>
    );
  }

  render() {
    const { isLoaded, user, balance, history } = this.state;
    const name = this.memberName();
    const contact = user ? user.email || user.mobile || "—" : "";

    return (
      <ConsolePage
        title={isLoaded ? name : "Loading member…"}
        subtitle={isLoaded ? `Loyalty member · ${contact}` : null}
        actions={
          <Btn kind="quiet" icon="arrow_back" onClick={() => this.props.history.push("/loyalty/accounts")}>
            All members
          </Btn>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        {!isLoaded ? (
          <Panel>
            <div style={{ padding: "40px 0", textAlign: "center", color: C.taupeLight }}>
              Loading member…
            </div>
          </Panel>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <Panel>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <Avatar name={name} size={46} solid />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 18, color: C.ink }}>{name}</div>
                  <div style={{ marginTop: 3, fontSize: 13, color: C.taupeLight }}>{contact}</div>
                </div>
                <Chip tone="neutral">
                  <span className="ms" style={{ fontSize: 16 }}>workspace_premium</span>
                  <span style={{ textTransform: "capitalize" }}>{balance.tier}</span>
                </Chip>
              </div>
            </Panel>

            <StatRow>
              <MiniStat dark label="Points balance" value={num(balance.pointsBalance)} hint="Usable across every branch" />
              <MiniStat label="Total earned" value={num(balance.totalEarned)} tone="good" />
              <MiniStat label="Total redeemed" value={num(balance.totalRedeemed)} />
              <MiniStat label="Lifetime spend" value={money(balance.lifetimeSpend)} />
            </StatRow>

            <div style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr", gap: 18, alignItems: "start" }}>
              <Panel style={{ padding: 0 }} bodyStyle={{ padding: "0 12px 8px" }}>
                <div style={{ padding: "22px 22px 4px" }}>
                  <div style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>Points history</div>
                  <div style={{ marginTop: 4, fontSize: 13, color: C.taupeLight }}>
                    {history.length} ledger entr{history.length === 1 ? "y" : "ies"}
                  </div>
                </div>
                <Table
                  columns={this.historyColumns()}
                  rows={history}
                  empty="No points have moved on this account yet"
                />
              </Panel>

              {this.renderAdjustPanel()}
            </div>
          </div>
        )}
      </ConsolePage>
    );
  }
}

export default LoyaltyAccountDetail;
